<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import format from 'date-fns/format';

import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

const rankings = ref([]);
const isLoading = ref(false);
const periodFilter = ref('7d');
const platformFilter = ref('');
const leadershipFilter = ref('');
const typeFilter = ref('');
const selectedLeader = ref(null);

const scoreBreakdown = computed(() => {
  if (!selectedLeader.value) return null;
  const l = selectedLeader.value;
  return {
    positive: [
      {
        label: 'Reposts no prazo',
        count: l.reposts_on_time || 0,
        points: 10,
        total: (l.reposts_on_time || 0) * 10,
      },
      {
        label: 'Alerta tratado',
        count: l.alerts_handled || 0,
        points: 3,
        total: (l.alerts_handled || 0) * 3,
      },
      {
        label: 'Posts autorais',
        count: l.authored_posts || 0,
        points: 8,
        total: (l.authored_posts || 0) * 8,
      },
      {
        label: 'Boa performance',
        count: l.good_performance || 0,
        points: 5,
        total: (l.good_performance || 0) * 5,
      },
      {
        label: 'Tratado com exclusao',
        count: l.exclusions_confirmed || 0,
        points: 10,
        total: (l.exclusions_confirmed || 0) * 10,
      },
    ],
    negative: [
      {
        label: 'Reposts atrasados',
        count: l.reposts_late || 0,
        points: -4,
        total: (l.reposts_late || 0) * -4,
      },
      {
        label: 'Pendencias vencidas',
        count: l.overdue_pending || 0,
        points: -10,
        total: (l.overdue_pending || 0) * -10,
      },
      {
        label: 'Alerta gerado',
        count: l.alerts_generated || 0,
        points: -20,
        total: (l.alerts_generated || 0) * -20,
      },
      {
        label: 'Perfis sem atividade',
        count: l.inactive_profiles || 0,
        points: -5,
        total: (l.inactive_profiles || 0) * -5,
      },
    ],
  };
});

function formatDate(dateStr) {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm');
  } catch {
    return dateStr;
  }
}

function pointsClass(points) {
  if (points > 0) return 'text-n-teal-11';
  if (points < 0) return 'text-n-ruby-11';
  return 'text-n-slate-11';
}

async function fetchRankings() {
  if (!props.selectedInbox) return;
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getLeadershipRanking({
      inboxId: props.selectedInbox.id,
      period: periodFilter.value,
      platform: platformFilter.value || undefined,
      leadership: leadershipFilter.value || undefined,
      type: typeFilter.value || undefined,
    });
    rankings.value = response.data || [];
    selectedLeader.value = null;
  } catch {
    rankings.value = [];
  } finally {
    isLoading.value = false;
  }
}

function selectLeader(leader) {
  selectedLeader.value = leader;
}

watch(
  () => props.selectedInbox,
  () => {
    if (props.selectedInbox) {
      fetchRankings();
    }
  }
);

onMounted(() => {
  if (props.selectedInbox) {
    fetchRankings();
  }
});
</script>

<template>
  <div>
    <div
      v-if="!selectedInbox"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_SELECT_INBOX') }}
    </div>

    <template v-else>
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-12"
      >
        <Spinner />
      </div>

      <template v-else>
        <!-- Filter row -->
        <div class="flex items-center gap-3 mb-4 flex-wrap">
          <select
            v-model="periodFilter"
            class="py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
          >
            <option value="7d">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_PERIOD_7D') }}
            </option>
            <option value="30d">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_PERIOD_30D') }}
            </option>
            <option value="3m">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_PERIOD_3M') }}
            </option>
          </select>
          <select
            v-model="platformFilter"
            class="py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
          >
            <option value="">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_PLATFORM_ALL') }}
            </option>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
          </select>
          <select
            v-model="leadershipFilter"
            class="py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
          >
            <option value="">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_LEADERSHIP_ALL') }}
            </option>
          </select>
          <select
            v-model="typeFilter"
            class="py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
          >
            <option value="">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_TYPE_ALL') }}
            </option>
          </select>
          <button
            class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors"
            @click="fetchRankings"
          >
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_FILTER_BUTTON') }}
          </button>
        </div>

        <!-- Export / Columns buttons -->
        <div class="flex items-center gap-2 mb-4">
          <button
            class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-slate-7 transition-colors"
          >
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_EXPORT') }}
          </button>
          <button
            class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-slate-7 transition-colors"
          >
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COLUMNS') }}
          </button>
        </div>

        <!-- Table -->
        <div class="bg-n-solid-2 rounded-lg border border-n-weak mb-6">
          <div v-if="rankings.length" class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-n-weak">
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_LEADERSHIP') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_TOTAL_POINTS') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_REPOSTS_ON_TIME') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_REPOSTS_LATE') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_OVERDUE') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_AUTHORED') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_GOOD_PERF') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_ALERTS_GENERATED') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_ALERTS_HANDLED') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_EXCLUSIONS') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_COL_LAST_ACTIVITY') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="leader in rankings"
                  :key="leader.id"
                  class="border-b border-n-weak cursor-pointer hover:bg-n-alpha-2 transition-colors"
                  :class="selectedLeader && selectedLeader.id === leader.id ? 'bg-n-blue-2' : ''"
                  @click="selectLeader(leader)"
                >
                  <td class="py-2 px-3 text-n-slate-12 font-medium">
                    @{{ leader.username }}
                  </td>
                  <td
                    class="py-2 px-3 text-center font-semibold"
                    :class="pointsClass(leader.total_points)"
                  >
                    {{ leader.total_points || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-12">
                    {{ leader.reposts_on_time || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-12">
                    {{ leader.reposts_late || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-12">
                    {{ leader.overdue_pending || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-12">
                    {{ leader.authored_posts || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-12">
                    {{ leader.good_performance || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-12">
                    {{ leader.alerts_generated || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-12">
                    {{ leader.alerts_handled || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-12">
                    {{ leader.exclusions_confirmed || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center text-xs text-n-slate-11">
                    {{ leader.last_activity_at ? formatDate(leader.last_activity_at) : '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-else
            class="flex items-center justify-center py-8 text-n-slate-11"
          >
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_EMPTY') }}
          </div>
        </div>

        <!-- Score details -->
        <div
          v-if="selectedLeader && scoreBreakdown"
          class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6"
        >
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_SCORE_DETAILS') }}
            — @{{ selectedLeader.username }}
          </h3>

          <!-- Positive scores -->
          <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div
              v-for="item in scoreBreakdown.positive"
              :key="'pos-' + item.label"
              class="rounded-lg p-3 border border-n-teal-7 bg-n-teal-2 text-center"
            >
              <p class="text-xs text-n-teal-11 mb-1">{{ item.label }}</p>
              <p class="text-lg font-semibold text-n-teal-11">
                {{ item.total }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ item.count }} x {{ item.points }}
                {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_POINTS') }}
              </p>
            </div>
          </div>

          <!-- Negative scores -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div
              v-for="item in scoreBreakdown.negative"
              :key="'neg-' + item.label"
              class="rounded-lg p-3 border border-n-ruby-7 bg-n-ruby-2 text-center"
            >
              <p class="text-xs text-n-ruby-11 mb-1">{{ item.label }}</p>
              <p class="text-lg font-semibold text-n-ruby-11">
                {{ item.total }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ item.count }} x {{ item.points }}
                {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_POINTS') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Info box -->
        <div class="bg-n-blue-2 rounded-lg p-3 border border-n-blue-7 text-xs text-n-blue-11">
          {{ $t('INSTAGRAM_MEDIA_REPORTS.RANKING_INFO_BOX') }}
        </div>
      </template>
    </template>
  </div>
</template>
